import type { PatientListItem } from '../lib/types';
import styles from './PatientRow.module.css';

interface PatientRowProps {
  patient: PatientListItem;
  selected: boolean;
  onSelect: (patient: PatientListItem) => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function statusLabel(status: PatientListItem['status']): string {
  if (status === 'attention') return 'Attenzione';
  if (status === 'risk') return 'A rischio';
  return 'Attivo';
}

function statusVariant(status: PatientListItem['status']): string {
  if (status === 'attention') return styles.statusAttention;
  if (status === 'risk') return styles.statusRisk;
  return styles.statusActive;
}

function formatWeight(
  kg?: number | null,
  delta?: number | null
): { value: string; delta: string; deltaClass: string } {
  if (kg == null) return { value: '—', delta: '', deltaClass: '' };
  const v = `${kg.toFixed(1)}kg`;
  if (delta == null || delta === 0) return { value: v, delta: '', deltaClass: '' };
  const sign = delta > 0 ? '+' : '';
  return {
    value: v,
    delta: `${sign}${delta.toFixed(1)}`,
    deltaClass: delta > 0 ? styles.deltaUp : styles.deltaDown,
  };
}

export function PatientRow({ patient, selected, onSelect }: PatientRowProps) {
  const w = formatWeight(patient.weight_kg, patient.weight_delta_kg);
  const adh = patient.adherence_7d_pct;
  const adhClass =
    adh == null ? '' : adh >= 80 ? styles.adhGood : adh >= 60 ? styles.adhOk : styles.adhBad;

  return (
    <button
      type="button"
      className={`${styles.row} ${selected ? styles.selected : ''}`}
      onClick={() => onSelect(patient)}
    >
      <div className={styles.avatar} aria-hidden>
        {initials(patient.name)}
      </div>

      <div className={styles.nameCell}>
        <span className={styles.name}>{patient.name}</span>
        {patient.human_id && <span className={styles.id}>{patient.human_id}</span>}
      </div>

      <div className={styles.planCell}>{patient.current_plan_label || '—'}</div>

      <div className={`${styles.adh} tnum ${adhClass}`}>
        {adh == null ? '—' : `${adh}%`}
      </div>

      <div className={`${styles.weight} tnum`}>
        <span>{w.value}</span>
        {w.delta && <span className={w.deltaClass}>{w.delta}</span>}
      </div>

      <div className={`${styles.statusBadge} ${statusVariant(patient.status)}`}>
        {statusLabel(patient.status)}
      </div>
    </button>
  );
}
